import React, { useRef } from 'react';
import { Datagrid, TextField, ReferenceField, useRefresh } from 'react-admin';

import { makeStyles } from '@material-ui/core/styles';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ClearIcon from '@material-ui/icons/Clear';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import ListAltIcon from '@material-ui/icons/ListAlt';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { useSnackbar } from 'notistack';

import { DialogoConfirmacao } from '../../Components';
import ActionsList from '../../Components/ReactAdmin/ActionsList';
import ActionsField from '../../Components/ReactAdmin/Fields/ActionsField';
import ListPageTabbed from '../../Components/ReactAdmin/List/ListPageTabbed';
import { ProtegerPagina } from '../../layout/VerificaPermissaoPage';
import { api } from '../../services';
import { PRIMARY_COLOR } from '../../utils';
import { STATUS_ATIVO, STATUS_INATIVO } from '../../utils/constants';
import { PERMISSAO_TELA_CATEGORIAS_LISTAR } from '../../utils/dadosTelas';
import DialogoCreateUpdate from './DialogoCreateUpdate';
import DialogoProdutos from './DialogoProdutos';
import DialogoShow from './DialogoShow';
import Filter from './Filter';

const useStyles = makeStyles(() => ({
  dataHeader: {
    flex: 1,
    backgroundColor: PRIMARY_COLOR,
    padding: '10px',
    color: 'white',
    fontWeight: 500,
    lineHeight: '1.5rem',
    fontSize: '0.875rem',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    borderSpacing: 0,
    borderCollapse: 'collapse',
  },
  dataCell: {
    flex: 1,
    padding: '10px',
    fontWeight: 500,
    lineHeight: '1.5rem',
    fontSize: '0.875rem',
    borderSpacing: 0,
    borderCollapse: 'collapse',
  },
}));

const tabs = [
  { id: 'ativos', name: 'Ativos', filter: { status: STATUS_ATIVO } },
  { id: 'inativos', name: 'Inativos', filter: { status: STATUS_INATIVO } },
];

const List = (props) => {
  const refDialogoConfirmacao = useRef(null);
  const refDialogo = useRef(null);
  const refDialogoShow = useRef(null);
  const refDialogoProdutos = useRef(null);
  const refresh = useRefresh();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  function handleCreate() {
    if (refDialogo.current) {
      refDialogo.current.handleCreate();
    }
  }

  function handleEdit({ id }) {
    if (refDialogo.current) {
      refDialogo.current.handleEdit(id);
    }
  }

  function handleShow({ id }) {
    if (refDialogoShow.current) {
      refDialogoShow.current.handleOpen(id);
    }
  }

  async function ativar(id) {
    await api.put(`/categorias/status/ativar/${id}`);
    handleClose();
  }

  async function desativar(id) {
    const data = await api.put(`/categorias/status/inativar/${id}`);
    if (data && data.data && !data.data.status) {
      enqueueSnackbar(data.data.message, { variant: 'error' });
      return;
    }
    handleClose();
  }

  async function deletar(id) {
    await api.put(`/categorias/status/deletar/${id}`);
    handleClose();
  }

  async function handleConfirma({ id, codigo }) {
    switch (codigo) {
      case 1:
        return ativar(id);
      case 2:
        return desativar(id);
      case 3:
        return deletar(id);
      default:
    }
  }

  function handleClose() {
    refresh();
  }

  function handleAtivar(id) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'REATIVAR CATEGORIA',
        'Tem certeza que deseja reativar esta categoria?',
        { id, codigo: 1 },
      );
    }
  }

  function handleInativar(id) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'INATIVAR CATEGORIA',
        'Tem certeza que deseja inativar esta categoria?',
        { id, codigo: 2 },
      );
    }
  }

  function handleVerProdutos({ id, nome }) {
    if (refDialogoProdutos.current) {
      refDialogoProdutos.current.handleOpen(id, nome);
    }
  }

  function handleDeletar(id) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'DELETAR CATEGORIA',
        'Tem certeza que deseja deletar esta categoria?',
        { id, codigo: 3 },
      );
    }
  }

  const toolbarActions = <ActionsList handleCriar={handleCreate} {...props} />;

  const renderGrid = () => (
    <Datagrid {...props}>
      <TextField
        source="nome"
        label="Descrição"
        headerClassName={classes.dataHeader}
        cellClassName={classes.dataCell}
        sortable={false}
      />
      <ReferenceField
        label="Setor"
        source="setor_id"
        reference="setores"
        sortable={false}
        linkType={false}
        headerClassName={classes.dataHeader}
        cellClassName={classes.dataCell}
      >
        <TextField source="nome" />
      </ReferenceField>
      <ActionsField
        textAlign="right"
        label="Ações"
        sortable={false}
        headerClassName={classes.dataHeader}
        cellClassName={classes.dataCell}
        actions={[
          {
            title: 'Editar',
            icon: <EditOutlinedIcon />,
            show: () => true,
            onClick: (r) => handleEdit(r),
          },
          {
            title: 'Detalhes',
            icon: <VisibilityIcon />,
            show: () => true,
            onClick: (r) => handleShow(r),
          },
          {
            title: 'Produtos',
            icon: <ListAltIcon />,
            show: () => true,
            onClick: (r) => handleVerProdutos(r),
          },
          {
            title: 'Ativar',
            icon: <CheckCircleOutlineIcon />,
            show: (r) => r.status === STATUS_INATIVO,
            onClick: (r) => handleAtivar(r.id),
          },
          {
            title: 'Desativar',
            icon: <ClearIcon />,
            show: (r) => r.status === STATUS_ATIVO,
            onClick: (r) => handleInativar(r.id),
          },
          {
            title: 'Excluir',
            icon: <DeleteOutlineIcon />,
            show: (r) => r.status === STATUS_INATIVO,
            onClick: (r) => handleDeletar(r.id),
          },
        ]}
      />
    </Datagrid>
  );

  return (
    <ProtegerPagina permissao={PERMISSAO_TELA_CATEGORIAS_LISTAR}>
      <>
        <ListPageTabbed
          title="Categorias de Produtos"
          filters={<Filter />}
          toolbarActions={toolbarActions}
          renderGrid={renderGrid}
          perPage={50}
          tabs={tabs}
          filterDefaultValues={{ status: STATUS_ATIVO }}
          listProps={{ sort: { field: 'nome', order: 'asc' }, ...props }}
        />

        <DialogoConfirmacao
          ref={refDialogoConfirmacao}
          handleConfirma={handleConfirma}
        />
        <DialogoCreateUpdate ref={refDialogo} handleClose={handleClose} />
        <DialogoShow ref={refDialogoShow} handleClose={handleClose} />
        <DialogoProdutos ref={refDialogoProdutos} />
      </>
    </ProtegerPagina>
  );
};

export default List;
